import { useEffect } from 'react';
import { KeyIcon } from './icons';

const AUTO_DISMISS_MS = 20_000;

interface ResetPairingDialogProps {
  open: boolean;
  kioskCode: string | null;
  apiUrl: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Technician-only confirm dialog; dismisses itself if nobody touches it. */
export function ResetPairingDialog({ open, kioskCode, apiUrl, onCancel, onConfirm }: ResetPairingDialogProps) {
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(onCancel, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [open, onCancel]);

  return (
    <div
      className={`fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
        open ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      aria-hidden={!open}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-title"
        className={`w-[min(80vmin,90vw)] rounded-[3vmin] bg-ink-2 p-[4vmin] ring-2 ring-line transition-transform duration-300 ${
          open ? 'scale-100' : 'scale-95'
        }`}
      >
        <div className="flex items-center gap-4">
          <KeyIcon className="size-[6vmin] text-accent" />
          <h2 id="reset-title" className="text-[3.6vmin] font-bold">
            Technician: reset pairing?
          </h2>
        </div>
        <p className="mt-4 text-[2.3vmin] leading-snug text-muted">
          This removes the saved kiosk key from this screen. You will need the key from the owner
          dashboard to pair it again.
        </p>
        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[2vmin]">
          <dt className="text-muted">Kiosk</dt>
          <dd className="font-mono">{kioskCode ?? '—'}</dd>
          <dt className="text-muted">API</dt>
          <dd className="truncate font-mono">{apiUrl}</dd>
        </dl>
        <div className="mt-[4vmin] flex justify-end gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-[1.6vmin] bg-white/8 px-[4vmin] py-[1.8vmin] text-[2.4vmin] font-semibold ring-1 ring-line active:bg-white/15"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-[1.6vmin] bg-bad px-[4vmin] py-[1.8vmin] text-[2.4vmin] font-bold text-ink active:brightness-90"
          >
            Reset pairing
          </button>
        </div>
      </div>
    </div>
  );
}
